import clsx from 'clsx'
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import HouseSearchResultsCardSkeletonCard from './house-search-results-card/house-search-results-card-skeleton/HouseSearchResultsCardSkeleton'
import { HouseSearchResultsCard } from './house-search-results-card/HouseSearchResultsCard'
import { HouseSearchResultsEmptyState } from '../house-search-results-empty-state/HouseSearchResultsEmptyState'
import { Icon } from '../../atoms'
import { CurrencyAreaContextProvider } from '../../contexts'
import { FilterDatesDto, HouseSearchFragment } from '../../../lib/graphql'
import { calcDays, calcNights } from '../../../utils'
import { IHouseExplorerUrlProps } from '../../../utils/house-explorer-url-params.helper'

interface IHouseSearchResultsProps {
  houses: HouseSearchFragment[] | undefined
  dates?: FilterDatesDto
  destination?: string | null
  createProposalClick: (house: HouseSearchFragment, index: number) => void
  className?: string
  hasCarousel?: boolean
  onHouseClick: (params: IHouseExplorerUrlProps) => void
  isEmptyStateFiltersDisplayed: boolean
  isMapMode?: boolean
  isLoading: boolean
  mapHoveredHouse?: string | undefined
  setMapHoveredHouse?: Dispatch<SetStateAction<string | undefined>>
  mapGoToRef?: string
}

export const HouseSearchResults = ({
  houses,
  dates,
  createProposalClick,
  className,
  hasCarousel = true,
  onHouseClick,
  isEmptyStateFiltersDisplayed,
  destination,
  isMapMode = false,
  isLoading,
  mapHoveredHouse,
  setMapHoveredHouse,
  mapGoToRef,
}: IHouseSearchResultsProps) => {
  const { numberOfNights, numberOfDays } = useMemo(() => {
    return dates?.startDate && dates?.endDate
      ? {
          numberOfNights: calcNights(dates.startDate, dates.endDate),
          numberOfDays: calcDays(dates.startDate, dates.endDate),
        }
      : {}
  }, [dates])

  // Refs for each house
  const houseRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    if (mapGoToRef && houseRefs.current[mapGoToRef]) {
      houseRefs.current[mapGoToRef]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [mapGoToRef])

  if (isLoading) {
    return (
      <div className={clsx(s.container, className)}>
        {Array(7)
          .fill('')
          .map((_, i) => (
            <HouseSearchResultsCardSkeletonCard key={`skeleton-card-${i}`} />
          ))}
      </div>
    )
  }

  if (houses === undefined) {
    return null
  }

  if (!houses.length) {
    const icon = <Icon size={24} color='#000000' name='domain-disabled' />
    const firstElement = (
      <p style={{ color: '#000000' }}>
        <FormattedMessage
          id='ui.HouseSearchResults.noResults'
          defaultMessage='Aucun lieu ne correspond à vos critères'
        />
      </p>
    )
    let secondElement = (
      <p style={{ color: '#707885' }}>
        <FormattedMessage
          id='ui.HouseSearchResults.modifySearch'
          defaultMessage='Essayez de modifier votre recherche'
        />
      </p>
    )

    if (isEmptyStateFiltersDisplayed) {
      secondElement = (
        <p style={{ color: '#707885' }}>
          <FormattedMessage
            id='ui.HouseSearchResults.modifyFilters'
            defaultMessage='Essayez de modifier ou supprimer des filtres'
          />
        </p>
      )
    }

    return (
      <HouseSearchResultsEmptyState
        icon={icon}
        firstElement={firstElement}
        secondElement={secondElement}
      />
    )
  }

  return (
    <div className={clsx(s.container, className)}>
      {houses.map((house, index) => {
        return (
          <CurrencyAreaContextProvider
            key={house._id}
            context={{ currencyArea: house.currencyArea }}
          >
            <div
              className={s.houseContainer}
              ref={(el) => {
                houseRefs.current[house._id] = el
              }}
            >
              <HouseSearchResultsCard
                house={house}
                index={index}
                numberOfNights={numberOfNights}
                numberOfDays={numberOfDays}
                createProposalClick={createProposalClick}
                hasCarousel={hasCarousel}
                onHouseClick={onHouseClick}
                destination={destination}
                isMapMode={isMapMode}
                mapHoveredHouse={mapHoveredHouse}
                setMapHoveredHouse={setMapHoveredHouse}
              />
            </div>
          </CurrencyAreaContextProvider>
        )
      })}
    </div>
  )
}
